import React, { useState, useEffect } from 'react';
import { PanelService } from '../../../network/panel/network';
import { countryNames, getCountryFlag } from '../../../constants/countries';
import './_keitaro-import-modal.scss';

interface KeitaroOffer {
  keitaroId: number;
  name: string;
  caption: string;
  geo: string;
  link: string;
  state: string;
  affiliateNetworkId: number;
  fullName: string;
}

interface SelectedOffer extends KeitaroOffer {
  offerwallType: 'MFO';
}

interface Props {
  onClose: () => void;
  onImported: () => void;
}

export const KeitaroImportModal: React.FC<Props> = ({ onClose, onImported }) => {
  const [offers, setOffers] = useState<KeitaroOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [selectedGeos, setSelectedGeos] = useState<Record<number, string>>({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: any[]; errors: string[] } | null>(null);
  const [search, setSearch] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await PanelService.getKeitaroUnsynced();
      setOffers(data.offers || []);
    } catch (e: any) {
      setError('Не вдалося завантажити офери з Keitaro');
    } finally {
      setLoading(false);
    }
  };

  const closeWithAnimation = () => {
    setIsClosing(true);
    setTimeout(() => onClose(), 200);
  };

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filteredOffers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredOffers.map(o => o.keitaroId)));
    }
  };

  const setGeoForOffer = (keitaroId: number, geo: string) => {
    setSelectedGeos(prev => ({ ...prev, [keitaroId]: geo.toUpperCase() }));
  };

  const getOfferGeo = (offer: KeitaroOffer) => (
    String(offer.geo || selectedGeos[offer.keitaroId] || '').trim().toUpperCase()
  );

  const handleImport = async () => {
    if (selected.size === 0) return;
    const missingGeo = offers.find(o => selected.has(o.keitaroId) && !getOfferGeo(o));
    if (missingGeo) {
      setImportResult({
        imported: [],
        errors: [`Оберіть GEO для офера #${missingGeo.keitaroId}`]
      });
      return;
    }
    setImporting(true);
    setImportResult(null);

    const toImport: SelectedOffer[] = offers
      .filter(o => selected.has(o.keitaroId))
      .map(o => ({
        ...o,
        geo: getOfferGeo(o),
        offerwallType: 'MFO' as const
      }));

    try {
      const result = await PanelService.importKeitaroOffers(toImport);
      setImportResult(result);
      if (result.imported?.length > 0) {
        onImported();
        // Remove imported offers from list
        const importedIdsList: number[] = result.imported.map((i: any) => i.keitaroId);
        const importedIds = new Set(importedIdsList);
        setOffers(prev => prev.filter(o => !importedIds.has(o.keitaroId)));
        setSelected(prev => {
          const next = new Set(prev);
          importedIdsList.forEach(id => next.delete(id));
          return next;
        });
      }
    } catch (e: any) {
      setImportResult({ imported: [], errors: ['Помилка імпорту: ' + (e.message || 'невідома')] });
    } finally {
      setImporting(false);
    }
  };

  const filteredOffers = offers.filter(o => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.fullName.toLowerCase().includes(q) ||
      o.caption.toLowerCase().includes(q) ||
      o.name.toLowerCase().includes(q) ||
      String(o.geo || '').toLowerCase().includes(q) ||
      String(o.keitaroId).includes(q);
  });

  const countryOptions = Object.entries(countryNames)
    .sort(([, left], [, right]) => left.localeCompare(right, 'uk'));
  const hasSelectedOfferWithoutGeo = offers.some(
    offer => selected.has(offer.keitaroId) && !getOfferGeo(offer)
  );

  return (
    <div className={`kim-backdrop ${isClosing ? 'kim-closing' : ''}`} onClick={closeWithAnimation}>
      <div className="kim-modal" onClick={e => e.stopPropagation()}>
        <div className="kim-header">
          <h2>Імпорт з Keitaro</h2>
          <button className="kim-close" onClick={closeWithAnimation}>&times;</button>
        </div>

        <div className="kim-toolbar">
          <input
            type="text"
            placeholder="Пошук..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="kim-search"
          />
          <div className="kim-toolbar-actions">
            <span className="kim-count">
              {loading ? 'Завантаження...' : `${filteredOffers.length} не синхронізовано`}
            </span>
          </div>
        </div>

        <div className="kim-table-wrap">
          {error && <div className="kim-error">{error}</div>}
          {loading ? (
            <div className="kim-loading">Завантаження оферів з Keitaro...</div>
          ) : filteredOffers.length === 0 ? (
            <div className="kim-empty">
              {search ? 'Нічого не знайдено' : 'Усі офери вже синхронізовані'}
            </div>
          ) : (
            <table className="kim-table">
              <thead>
                <tr>
                  <th className="kim-th-check">
                    <input
                      type="checkbox"
                      checked={selected.size === filteredOffers.length && filteredOffers.length > 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="kim-th-id">ID</th>
                  <th className="kim-th-name">Назва</th>
                  <th className="kim-th-geo">Гео</th>
                  <th className="kim-th-state">Стан</th>
                </tr>
              </thead>
              <tbody>
                {filteredOffers.map(offer => (
                  <tr
                    key={offer.keitaroId}
                    className={selected.has(offer.keitaroId) ? 'kim-row-selected' : ''}
                    onClick={() => toggleSelect(offer.keitaroId)}
                  >
                    <td className="kim-td-check">
                      <input
                        type="checkbox"
                        checked={selected.has(offer.keitaroId)}
                        onChange={() => toggleSelect(offer.keitaroId)}
                        onClick={e => e.stopPropagation()}
                      />
                    </td>
                    <td className="kim-td-id">{offer.keitaroId}</td>
                    <td className="kim-td-name">
                      <div className="kim-offer-name">{offer.caption || offer.name}</div>
                      {offer.caption && offer.caption !== offer.name && (
                        <div className="kim-offer-subname">{offer.name}</div>
                      )}
                    </td>
                    <td className="kim-td-geo" onClick={e => e.stopPropagation()}>
                      {offer.geo ? (
                        <span className="kim-geo-badge">{offer.geo}</span>
                      ) : (
                        <select
                          className={`kim-geo-select${selected.has(offer.keitaroId) && !selectedGeos[offer.keitaroId] ? ' kim-geo-select-required' : ''}`}
                          value={selectedGeos[offer.keitaroId] || ''}
                          onChange={event => setGeoForOffer(offer.keitaroId, event.target.value)}
                          aria-label={`GEO офера ${offer.keitaroId}`}
                        >
                          <option value="">Оберіть GEO</option>
                          {countryOptions.map(([code, name]) => (
                            <option value={code} key={code}>
                              {getCountryFlag(code)} {name} ({code})
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="kim-td-state">
                      <span className={`kim-state ${offer.state === 'active' ? 'kim-state-active' : 'kim-state-disabled'}`}>
                        {offer.state === 'active' ? 'Активний' : 'Вимкнений'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {importResult && (
          <div className="kim-result">
            {importResult.imported.length > 0 && (
              <div className="kim-result-success">
                Імпортовано: {importResult.imported.length} оферів
              </div>
            )}
            {importResult.errors.length > 0 && (
              <div className="kim-result-errors">
                {importResult.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
          </div>
        )}

        <div className="kim-footer">
          <button className="kim-btn kim-btn-cancel" onClick={closeWithAnimation}>
            Закрити
          </button>
          <button
            className="kim-btn kim-btn-import"
            onClick={handleImport}
            disabled={selected.size === 0 || importing || hasSelectedOfferWithoutGeo}
            title={hasSelectedOfferWithoutGeo ? 'Оберіть GEO для всіх вибраних оферів' : undefined}
          >
            {importing ? 'Імпортую...' : `Імпортувати (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
};
